<template>
    <div class="documents-container white">
        <v-row no-gutters>
            <v-col cols="12">

                <div v-if="isLoading" class="text-center mb-12">
                    <!-- Skeleton para los filtros -->
                    <v-card class="mb-6 rounded-0" flat color="transparent">
                        <v-card-text class="py-2 px-0">
                            <div class="d-flex align-center">
                                <v-skeleton-loader type="image" width="500" height="40" class="mr-4"
                                    style="background-color: white; border-radius: 13px;"></v-skeleton-loader>
                                <v-spacer></v-spacer>
                                <v-skeleton-loader type="image" width="80" height="40" class="mr-4"
                                    style="background-color: white; border-radius: 13px;"></v-skeleton-loader>
                                <v-skeleton-loader type="image" width="200" height="40"
                                    style="background-color: white; border-radius: 13px;"></v-skeleton-loader>
                            </div>
                        </v-card-text>
                    </v-card>

                    <!-- Skeleton para el grid de documentos -->
                    <v-row>
                        <v-col cols="12" sm="6" md="4" v-for="n in 12" :key="n" class="pa-2">
                            <v-skeleton-loader class="mx-auto rounded-lg" type="card" height="150"></v-skeleton-loader>
                        </v-col>
                    </v-row>
                </div>

                <div v-else-if="error" class="text-center my-12">
                    <v-alert type="error" outlined>
                        {{ error }}
                    </v-alert>
                    <v-btn color="primary" class="mt-4" @click="fetchDocuments">
                        Intentar de nuevo
                    </v-btn>
                </div>

                <v-row v-else>
                    <v-col cols="12">
                        <!-- Filtros y búsqueda -->
                        <v-card 
                        v-if="!isLoading && (myDocuments.length > 0 || sharedDocuments.length > 0 || communityDocuments.length > 0 || recentlyViewedDocuments.length > 0)"
                        class="mb-6 rounded-0" flat color="transparent">
                            <v-card-text class="py-2 px-0">
                                <div class="d-flex align-center px-6"
                                :class="{'flex-column': $vuetify.breakpoint.xs}"
                                >
                                    <v-text-field v-model="searchQuery" label="Buscar apuntes"
                                        prepend-inner-icon="$search" dense solo flat hide-details clearable
                                        class="search-field" color="#373837"
                                        style="border-radius: 13px;"
                                        :style="{'width': $vuetify.breakpoint.xs ? '100%' : ''}"
                                        @input="filterAndSortLists"></v-text-field>
                                    <v-spacer v-if="!$vuetify.breakpoint.xs"></v-spacer>
                                    <v-btn-toggle 
                                    v-if="!$vuetify.breakpoint.xs"
                                    class="mr-4 rounded-xl"
                                        style="background-color: #ededed6e; padding: 4px 5px;">
                                        <v-btn small :color="viewMode === 'grid' ? 'white' : 'transparent'" 
                                        @click="viewMode = 'grid'" :ripple="false"
                                        :style="viewMode === 'grid' ? 'outline: rgb(218 210 181 / 26%) solid 1px !important' : ''"
                                        style="margin-right: 2px; border: 0 !important">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                                                viewBox="0 0 24 24" fill="none"
                                                :stroke="viewMode === 'grid' ? '#1E1E1E' : '#4a4a4a'" stroke-width="2"
                                                stroke-linecap="round" stroke-linejoin="round"
                                                class="lucide lucide-layout-grid-icon lucide-layout-grid">
                                                <rect width="7" height="7" x="3" y="3" rx="1" />
                                                <rect width="7" height="7" x="14" y="3" rx="1" />
                                                <rect width="7" height="7" x="14" y="14" rx="1" />
                                                <rect width="7" height="7" x="3" y="14" rx="1" />
                                            </svg>
                                        </v-btn>
                                        <v-btn small :color="viewMode === 'list' ? 'white' : 'transparent'" 
                                        :style="(viewMode === 'list' ? 'outline: rgb(218 210 181 / 26%) solid 1px !important; ' : '')"
                                        @click="viewMode = 'list'" :ripple="false"
                                        style="margin-left: 1px; border: 0 !important" depressed>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                                                viewBox="0 0 24 24" fill="none"
                                                :stroke="viewMode === 'list' ? '#1E1E1E' : '#4a4a4a'" stroke-width="2"
                                                stroke-linecap="round" stroke-linejoin="round"
                                                class="lucide lucide-align-justify-icon lucide-align-justify">
                                                <path d="M3 12h18" />
                                                <path d="M3 18h18" />
                                                <path d="M3 6h18" />
                                            </svg>
                                        </v-btn>
                                    </v-btn-toggle>
                                    <v-select 
                                    v-model="sortOption" :items="sortOptions" label="Ordenar por"
                                        max-width="200" style="border-radius: 13px;"
                                        class="font-rubik select-sort" background-color="#ededed6e" dense color="#373837" hide-details solo flat
                                        outlined content-class="sort-select rounded-xl"
                                        :class="$vuetify.breakpoint.xs ? 'mt-2' : ''" 
                                        :style="$vuetify.breakpoint.xs ? 'width: 100%' : 'max-width: 200px'"
                                        @change="filterAndSortLists"></v-select>
                                </div>
                            </v-card-text>
                        </v-card>

                        <!-- Mensaje si no hay ningún documento en absoluto -->
                         <v-card v-if="!isLoading && noDocumentsAtAll && !subjectId" class="mb-6 rounded-0 mx-auto py-3 px-5 rounded-lg" flat color="rgb(235 235 235 / 58%)" width="fit-content">
                            <v-card-text class="py-2 px-0">
                                <div class="text-center d-flex flex-column align-center font-rubik">
                                    <span style="font-size: 20px;" class="font-weight-medium py-4">No tienes apuntes</span>
                                    <span style="font-size: 14px;" class="grey--text">Sube un apunte o espera a que alguien comparta uno contigo.</span>
                                </div>
                            </v-card-text>
                        </v-card>
                        
                        <!-- Sección Mis Apuntes -->
                        <document-display-section
                            v-if="subjectId || (!isLoading && myDocuments.length > 0) || (!isLoading && !noDocumentsAtAll)"
                            title="Mis Apuntes"
                            :documents="filteredMyDocuments"
                            :all-documents-in-section="myDocuments"
                            :view-mode="viewMode"
                            :search-query="searchQuery"
                            :is-loading="isLoading"
                            :headers="headers"
                            :show-author="false"
                            :can-delete="true"
                            :can-rename="true"
                            empty-message="Aún no has subido ningún apunte"
                            :empty-description="subjectId ? 'Añade apuntes a esta asignatura para empezar' : 'Añade apuntes para empezar a trabajar'"
                            @view="viewDocument"
                            @rename="renameDocument"
                            @share="shareDocument"
                            @copy="createCopy"
                            @delete="showDeleteDialog"
                            @toggle-privacy="toggleDocumentPrivacy"
                        >
                        </document-display-section>

                        <!-- Sección Apuntes Recientemente Vistos -->
                        <document-display-section
                            v-if="!isLoading && recentlyViewedDocuments.length > 0 && $root.currentUser && $root.currentUser.userId"
                            title="Apuntes Recientemente Vistos"
                            :documents="filteredRecentlyViewedDocuments"
                            :all-documents-in-section="recentlyViewedDocuments"
                            :view-mode="viewMode"
                            :search-query="searchQuery"
                            :is-loading="isLoading"
                            :headers="headersShared"
                            :show-author="true"
                            :can-delete="false"
                            :can-rename="false"
                            empty-message="No has visto ningún apunte recientemente"
                            empty-description="Los apuntes que abras aparecerán aquí para un acceso rápido"
                            @view="viewDocument"
                            @share="shareDocument"
                            @copy="createCopy"
                        >
                            <template v-slot:image-collection="{ width, height }">
                                <Images :width="width" :height="height" />
                            </template>
                            <template v-slot:presentation="{ width, height }">
                                <Presentation :width="width" :height="height" />
                            </template>
                        </document-display-section>

                        <!-- Sección Apuntes de la Comunidad -->
                        <document-display-section
                            v-if="!isLoading && communityDocuments.length > 0"
                            title="Apuntes de la Comunidad"
                            :documents="filteredCommunityDocuments"
                            :all-documents-in-section="communityDocuments"
                            :view-mode="viewMode"
                            :search-query="searchQuery"
                            :is-loading="isLoading"
                            :headers="headersShared"
                            :show-author="true"
                            :can-delete="false"
                            :can-rename="false"
                            :empty-message="subjectId ? 'No hay apuntes de la comunidad en esta asignatura' : 'No hay apuntes de la comunidad disponibles'"
                            :empty-description="subjectId ? 'Los apuntes de la comunidad de esta asignatura se mostrarán aquí' : 'Los apuntes de la comunidad se mostrarán aquí'"
                            :most-popular-id="mostPopularCommunityDocumentId"
                            :most-studied-id="mostStudiedCommunityDocumentId"
                            @view="viewDocument"
                            @share="shareDocument"
                            @copy="createCopy"
                        >
                            <template v-slot:image-collection="{ width, height }">
                                <Images :width="width" :height="height" />
                            </template>
                            <template v-slot:presentation="{ width, height }">
                                <Presentation :width="width" :height="height" />
                            </template>
                        </document-display-section>


                           <!-- Sección Apuntes Compartidos Conmigo -->
                           <document-display-section
                            v-if="!isLoading && (sharedDocuments.length > 0)"
                            title="Apuntes Compartidos Conmigo"
                            :documents="filteredSharedDocuments"
                            :all-documents-in-section="sharedDocuments"
                            :view-mode="viewMode"
                            :search-query="searchQuery"
                            :is-loading="isLoading"
                            :headers="headersShared"
                            :show-author="true"
                            :can-delete="false"
                            :can-rename="false"
                            :empty-message="subjectId ? 'No hay apuntes compartidos en esta asignatura' : 'Nadie ha compartido apuntes contigo aún'"
                            :empty-description="subjectId ? 'Cuando alguien comparta un apunte de esta asignatura, aparecerá aquí' : 'Cuando alguien comparta un apunte, aparecerá aquí.'"
                            @view="viewDocument"
                            @share="shareDocument"
                            @copy="createCopy"
                        >
                            <template v-slot:image-collection="{ width, height }">
                                <Images :width="width" :height="height" />
                            </template>
                            <template v-slot:presentation="{ width, height }">
                                <Presentation :width="width" :height="height" />
                            </template>
                        </document-display-section>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>

        <!-- Diálogo de eliminación -->
        <v-dialog v-model="deleteDialog" max-width="400">
            <v-card>
                <v-card-title class="headline">Eliminar material</v-card-title>
                <v-card-text>
                    ¿Estás seguro de que deseas eliminar <strong>{{ selectedDocument.name }}</strong>? Esta acción no
                    se puede
                    deshacer.
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="deleteDialog = false">Cancelar</v-btn>
                    <v-btn color="error" 
                    :loading="isDeleting"
                    text @click="deleteDocument">Eliminar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Diálogo de compartir -->
        <share-document-dialog 
            v-model="showShareDialog"
            :document-id="selectedDocument.id" 
            :document-name="selectedDocument.name"
        />

        <!-- Diálogo de renombrar documento -->
        <v-dialog v-model="showRenameDialog" max-width="500">
            <v-card>
                <v-card-title class="headline">Renombrar apunte</v-card-title>
                <v-card-text>
                    <v-text-field v-model="newDocumentName" label="Nuevo nombre" outlined autofocus
                        @keyup.enter="saveNewName"></v-text-field>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="showRenameDialog = false">Cancelar</v-btn>
                    <v-btn color="primary" :disabled="!newDocumentName" @click="saveNewName">
                        Guardar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import DocumentActionsMenu from './DocumentActionsMenu.vue';
import Images from '@/assets/icons/Images.vue';
import Presentation from '@/assets/icons/Presentation.vue';
import ShareDocumentDialog from './ShareDocumentDialog.vue';
import Search from "/node_modules/lucide-vue/dist/esm/icons/search.js"
import DocumentDisplaySection from './DocumentDisplaySection.vue';

export default {
    name: 'ViewDocuments',
    components: {
        DocumentActionsMenu,
        Images,
        Presentation,
        ShareDocumentDialog,
        LucideSearch: Search,
        DocumentDisplaySection
    },
    props: {
        subjectId: {
            type: [Number, String],
            default: null
        }
    },
    data() {
        return {
            viewMode: 'grid',
            myDocuments: [],
            filteredMyDocuments: [],
            sharedDocuments: [],
            filteredSharedDocuments: [],
            communityDocuments: [],
            filteredCommunityDocuments: [],
            recentlyViewedDocuments: [],
            filteredRecentlyViewedDocuments: [],
            isLoading: true,
            error: null,
            searchQuery: '',
            sortOption: 'most_viewed',
            headers: [
                { text: '', value: 'icon', sortable: false, width: '30px', class: 'icon-column-table' },
                { text: 'Título', value: 'name', sortable: true },
                { text: 'Páginas', value: 'num_pages', sortable: true, width: '80px' },
                { text: 'Fecha de creación', value: 'created_at', sortable: true },
                { text: 'Acciones', value: 'actions', sortable: false, align: 'center' }
            ],
            headersShared: [
                { text: '', value: 'icon', sortable: false, width: '30px', class: 'icon-column-table' },
                { text: 'Título', value: 'name', sortable: true },
                { text: 'Autor', value: 'author', sortable: true },
                { text: 'Páginas', value: 'num_pages', sortable: true, width: '80px' },
                { text: 'Fecha de creación', value: 'created_at', sortable: true },
                { text: 'Acciones', value: 'actions', sortable: false, align: 'center' }
            ],
            documentTypes: [
                { text: 'Documento', value: 'document' },
                { text: 'Presentación', value: 'presentation' },
                { text: 'Resumen', value: 'summary' },
                { text: 'Notas', value: 'notes' }
            ],
            sortOptions: [
                { text: 'Más estudiado', value: 'most_viewed' },
                { text: 'Más reciente', value: 'newest' },
                { text: 'Alfabético (A-Z)', value: 'az' },
                { text: 'Alfabético (Z-A)', value: 'za' }
            ],
            deleteDialog: false,
            selectedDocument: {},
            fileToUpload: null,
            newDocumentName: '',
            newDocumentDescription: '',
            newDocumentType: 'document',
            isUploading: false,
            fileRules: [
                v => !!v || 'El archivo es requerido',
                v => !v || v.size < 20000000 || 'El archivo debe ser menor a 20MB'
            ],
            shareDialog: false,
            shareEmail: '',
            sharedEmails: [],
            showRenameDialog: false,
            showShareDialog: false,
            isDeleting: false
        };
    },
    computed: {
        isUploadFormValid() {
            return this.fileToUpload && this.newDocumentName && this.newDocumentType;
        },
        displayMyDocuments() {
            return this.filteredMyDocuments.map(doc => ({
                ...doc,
                displayName: doc.name || doc.title || doc.original_name
            }));
        },
        displaySharedDocuments() {
            return this.filteredSharedDocuments.map(doc => ({
                ...doc,
                displayName: doc.name || doc.title || doc.original_name
            }));
        },
        displayCommunityDocuments() {
            return this.filteredCommunityDocuments.map(doc => ({
                ...doc,
                displayName: doc.name || doc.title || doc.original_name
            }));
        },
        noDocumentsAtAll() {
            return !this.isLoading && this.myDocuments.length === 0 && this.sharedDocuments.length === 0 && this.communityDocuments.length === 0 && this.recentlyViewedDocuments.length === 0;
        },
        noCommunityDocumentsAfterFilter() {
            return !this.isLoading && this.communityDocuments.length > 0 && this.filteredCommunityDocuments.length === 0 && this.searchQuery;
        },
        mostPopularCommunityDocumentId() {
            if (!this.filteredCommunityDocuments || this.filteredCommunityDocuments.length === 0) {
                return null;
            }
            
            // Encontrar el apunte con mayor popularity_score
            const mostPopular = this.filteredCommunityDocuments.reduce((max, doc) => {
                return (doc.popularity_score || 0) > (max.popularity_score || 0) ? doc : max;
            });
            
            return mostPopular.id;
        },
        mostStudiedCommunityDocumentId() {
            if (!this.filteredCommunityDocuments || this.filteredCommunityDocuments.length === 0) {
                return null;
            }
            
            // Encontrar el apunte con mayor total_time_all_users
            const mostStudied = this.filteredCommunityDocuments.reduce((max, doc) => {
                return (doc.total_time_all_users || 0) > (max.total_time_all_users || 0) ? doc : max;
            });
            
            return mostStudied.id;
        }
    },
    watch: {
        subjectId: {
            handler() {
                this.fetchDocuments();
            },
            immediate: true
        }
    },
    methods: {
        async fetchDocuments() {
            this.isLoading = true;
            this.error = null;
            let responseMyDocs;
            let responseSharedDocs;
            let responseCommunityDocs;

            const processDocArray = (docsArray) => {
                return docsArray.map(doc => {
                    if (doc.preview_image && !doc.preview_image.startsWith('data:')) {
                        doc.preview_image = `data:image/jpeg;base64,${doc.preview_image}`;
                    }
                    if (!doc.title && doc.original_name) {
                        doc.title = doc.original_name;
                    }
                    return {
                        ...doc,
                        name: doc.name || doc.title || doc.original_name,
                        view_time_seconds: doc.view_time_seconds || 0,
                        last_viewed_at: doc.last_viewed_at || null,
                        view_count: doc.view_count || 0,
                        total_time_all_users: doc.total_time_all_users || 0,
                        unique_users_count: doc.unique_users_count || 0,
                        popularity_score: doc.popularity_score || 0,
                        total_blocks: doc.total_blocks || 0,
                        completed_items: doc.completed_items || 0,
                        total_items: doc.total_items || 0,
                        progress_percentage: doc.progress_percentage || 0
                    };
                });
            };

            try {
                if (this.subjectId) {
                    console.log("this.$root.token.value", this.$root.token.value)
                    // Usar el nuevo endpoint que devuelve todos los tipos de apuntes filtrados por asignatura
                    const response = await this.axios.get(`/subject-documents/${this.subjectId}/complete`, {
                        headers: { Authorization: this.$root.token.value || "NO_VALUE" }
                    });
                    
                    if (response.data.success) {
                        this.myDocuments = processDocArray(response.data.myDocuments || []);
                        this.sharedDocuments = processDocArray(response.data.sharedDocuments || []);
                        this.communityDocuments = processDocArray(response.data.communityDocuments || []);
                        this.recentlyViewedDocuments = processDocArray(response.data.recentlyViewedDocuments || []);
                    } else {
                        this.myDocuments = [];
                        this.sharedDocuments = [];
                        this.communityDocuments = [];
                        this.recentlyViewedDocuments = [];
                        this.error = response.data.error || 'No se pudieron cargar los apuntes de la asignatura';
                    }
                } else {
                    // Obtener mis documentos
                    responseMyDocs = await this.axios.get('/documents/my-documents', {
                        headers: { Authorization: this.$root.token.value }
                    });
                    if (responseMyDocs.data.success) {
                        this.myDocuments = processDocArray(responseMyDocs.data.documents);
                    } else {
                        this.myDocuments = [];
                        this.error = responseMyDocs.data.error || 'No se pudieron cargar tus apuntes';
                    }

                    // Obtener documentos compartidos conmigo
                    responseSharedDocs = await this.axios.get('/documents/shared-with-me', {
                        headers: { Authorization: this.$root.token.value }
                    });
                    if (responseSharedDocs.data.success) {
                        this.sharedDocuments = processDocArray(responseSharedDocs.data.documents);
                    } else {
                        this.sharedDocuments = [];
                        console.error('Error al cargar apuntes compartidos:', responseSharedDocs.data.error);
                    }

                    // Obtener apuntes de la comunidad
                    responseCommunityDocs = await this.axios.get('/documents/community', {
                        headers: { Authorization: this.$root.token.value }
                    });
                    if (responseCommunityDocs.data.success) {
                        this.communityDocuments = processDocArray(responseCommunityDocs.data.documents);
                    } else {
                        this.communityDocuments = [];
                        console.error('Error al cargar apuntes de la comunidad:', responseCommunityDocs.data.error);
                    }
                    
                    // En la vista general, los apuntes recientemente vistos no están disponibles aún
                    this.recentlyViewedDocuments = [];
                }
                
                this.filterAndSortLists();
            } catch (err) {
                console.error('Error al cargar los apuntes:', err);
                this.error = 'Error al cargar los apuntes. Por favor, inténtalo de nuevo.';
                this.myDocuments = [];
                this.sharedDocuments = [];
                this.communityDocuments = [];
                this.recentlyViewedDocuments = [];
                this.filterAndSortLists();
            } finally {
                this.isLoading = false;
            }
        },

        filterAndSortLists() {
            const applyFiltersAndSort = (docsList) => {
                let filtered = [...docsList];

                if (this.searchQuery) {
                    const query = this.searchQuery.toLowerCase();
                    filtered = filtered.filter(doc =>
                        (doc.name && doc.name.toLowerCase().includes(query)) ||
                        (doc.title && doc.title.toLowerCase().includes(query)) ||
                        (doc.description && doc.description.toLowerCase().includes(query)) ||
                        (doc.author && doc.author.toLowerCase().includes(query))
                    );
                }

                switch (this.sortOption) {
                    case 'newest':
                        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                        break;
                    case 'most_viewed':
                        // Usar la puntuación de popularidad para todos los tipos de apuntes
                        filtered.sort((a, b) => (b.popularity_score || 0) - (a.popularity_score || 0));
                        break;
                    case 'az':
                        filtered.sort((a, b) => (a.name || a.title).localeCompare(b.name || b.title));
                        break;
                    case 'za':
                        filtered.sort((a, b) => (b.name || b.title).localeCompare(a.name || a.title));
                        break;
                }
                return filtered;
            };

            this.filteredMyDocuments = applyFiltersAndSort(this.myDocuments);
            this.filteredSharedDocuments = applyFiltersAndSort(this.sharedDocuments);
            this.filteredCommunityDocuments = applyFiltersAndSort(this.communityDocuments);
            this.filteredRecentlyViewedDocuments = applyFiltersAndSort(this.recentlyViewedDocuments);
        },

        getIcon(type) {
            switch (type) {
                case 'document':
                    return 'mdi-file-document';
                case 'presentation':
                    return 'mdi-file-presentation-box';
                case 'summary':
                    return 'mdi-text-box';
                case 'notes':
                    return 'mdi-note-text';
                default:
                    return 'mdi-file';
            }
        },

        getIconColor(type) {
            switch (type) {
                case 'document':
                    return 'blue';
                case 'presentation':
                    return 'orange';
                case 'summary':
                    return 'green';
                case 'notes':
                    return 'purple';
                default:
                    return 'grey';
            }
        },
        formatDate(dateString) {
            if (!dateString) return '';
            
            const date = new Date(dateString);
            
            date.setHours(date.getHours() - 2);

            if (isNaN(date.getTime())) return '';
            
            const now = new Date();
            
            
            const diff = now - date;
            
            const seconds = Math.floor(diff / 1000);
            const minutes = Math.floor(seconds / 60);
            const hours = Math.floor(minutes / 60);
            const days = Math.floor(hours / 24);
            const weeks = Math.floor(days / 7);
            const months = Math.floor(days / 30.44);
            const years = Math.floor(days / 365.25);

            if (seconds < 60) {
                return `hace ${seconds} s`;
            } else if (minutes < 60) {
                return `hace ${minutes} min`;
            } else if (hours < 24) {
                return `hace ${hours} h`;
            } else if (days < 7) {
                return `hace ${days} d`;
            } else if (weeks < 4.345) {
                return `hace ${weeks} sem`;
            } else if (months < 12) {
                return `hace ${months} m`;
            } else {
                return `hace ${years} a`;
            }
        },

        viewDocument(document) {
            this.$router.push(`/app/document/${document.id}`);
        },

        showDeleteDialog(document) {
            this.selectedDocument = document;
            this.deleteDialog = true;
        },

        async deleteDocument() {
            this.isDeleting = true;
            try {
                const response = await this.axios.delete(`/documents/${this.selectedDocument.id}`, {
                    headers: {
                        Authorization: this.$root.token.value
                    }
                });

                if (response.data.success) {
                    this.myDocuments = this.myDocuments.filter(doc => doc.id !== this.selectedDocument.id);
                    this.filterAndSortLists();
                } else {
                    this.$toast.error(response.data.error || 'No se pudo eliminar el material');
                }
            } catch (err) {
                console.error('Error al eliminar el material:', err);
                this.$toast.error('Error al eliminar el material');
            } finally {
                this.deleteDialog = false;
                this.isDeleting = false;
            }
        },

        async uploadDocument() {
            if (!this.isUploadFormValid) return;

            this.isUploading = true;

            try {
                const formData = new FormData();
                formData.append('file', this.fileToUpload);
                formData.append('title', this.newDocumentName || this.newDocumentTitle);
                formData.append('description', this.newDocumentDescription || '');
                formData.append('type', this.newDocumentType);
                formData.append('name', this.newDocumentName || this.newDocumentTitle);
                formData.append('userId', this.$root.currentUser.userId);

                const response = await this.axios.post('/documents/upload', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        Authorization: this.$root.token.value
                    }
                });

                if (response.data.success) {
                    this.myDocuments.unshift(response.data.document);
                    this.filterAndSortLists();
                    this.$toast.success('Material subido con éxito');
                    this.resetUploadForm();
                } else {
                    this.$toast.error(response.data.error || 'No se pudo subir el material');
                }
            } catch (err) {
                console.error('Error al subir el material:', err);
                this.$toast.error('Error al subir el material');
            } finally {
                this.isUploading = false;
            }
        },

        resetUploadForm() {
            this.fileToUpload = null;
            this.newDocumentName = '';
            this.newDocumentDescription = '';
            this.newDocumentType = 'document';
        },

        async downloadDocument(document) {
            try {
                const response = await this.axios.get(`/documents/${document.id}/download`, {
                    headers: {
                        Authorization: this.$root.token.value
                    },
                    responseType: 'blob'
                });

                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${document.title}.${this.getFileExtension(document.type)}`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            } catch (err) {
                console.error('Error al descargar el material:', err);
                this.$toast.error('Error al descargar el material');
            }
        },

        getFileExtension(type) {
            switch (type) {
                case 'document':
                    return 'docx';
                case 'presentation':
                    return 'pptx';
                case 'summary':
                    return 'pdf';
                case 'notes':
                    return 'txt';
                default:
                    return 'pdf';
            }
        },

        shareDocument(document) {
            this.selectedDocument = document;
            this.shareEmail = '';
            this.shareDialog = true;
            this.showShareDialog = true;
        },

        renameDocument(document) {
            this.selectedDocument = document;
            this.newDocumentName = document.name || document.title;
            this.showRenameDialog = true;
        },

        async saveNewName() {
            const newName = this.newDocumentName.trim();
            if (!newName) return;

            try {
                const response = await this.axios.put(`/document/${this.selectedDocument.id}/rename`, {
                    name: newName
                }, {
                    headers: {
                        Authorization: this.$root.token.value
                    }
                });

                if (response.data.success) {
                    const myDocIndex = this.myDocuments.findIndex(doc => doc.id === this.selectedDocument.id);
                    if (myDocIndex !== -1) {
                        this.myDocuments[myDocIndex].name = response.data.newName;
                        if (this.myDocuments[myDocIndex].title !== undefined) {
                            this.myDocuments[myDocIndex].title = response.data.newName;
                        }
                    }
                    const sharedDocIndex = this.sharedDocuments.findIndex(doc => doc.id === this.selectedDocument.id);
                    if (sharedDocIndex !== -1) {
                        this.sharedDocuments[sharedDocIndex].name = response.data.newName;
                        if (this.sharedDocuments[sharedDocIndex].title !== undefined) {
                            this.sharedDocuments[sharedDocIndex].title = response.data.newName;
                        }
                    }
                    this.filterAndSortLists();
                    this.$toast.success('Documento renombrado con éxito');
                } else {
                    this.$toast.error(response.data.error || 'No se pudo renombrar el documento');
                }
            } catch (err) {
                console.error('Error al renombrar el documento:', err);
                this.$toast.error('Error al renombrar el documento');
            } finally {
                this.showRenameDialog = false;
            }
        },

        async createCopy(document) {
            try {
                const response = await this.axios.post(`/documents/${document.id}/copy`, {}, {
                    headers: {
                        Authorization: this.$root.token.value
                    }
                });

                if (response.data.success) {
                    this.myDocuments.unshift(response.data.document);
                    this.filterAndSortLists();
                    this.$toast.success('Copia creada con éxito');
                } else {
                    this.$toast.error(response.data.error || 'No se pudo crear una copia');
                }
            } catch (err) {
                console.error('Error al crear copia:', err);
                this.$toast.error('Error al crear una copia del documento');
            }
        },

        async toggleDocumentPrivacy(document) {
            try {
                // Determinar el nuevo estado de privacidad
                const isCurrentlyPrivate = document.show_community === false || document.show_community === 0;
                const newPrivateState = !isCurrentlyPrivate;
                
                const response = await this.axios.put(`/document/${document.id}/privacy`, {
                    isPrivate: newPrivateState
                }, {
                    headers: {
                        Authorization: this.$root.token.value
                    }
                });

                if (response.data.success) {
                    // Actualizar el documento en el array local
                    const myDocIndex = this.myDocuments.findIndex(doc => doc.id === document.id);
                    if (myDocIndex !== -1) {
                        this.myDocuments[myDocIndex].show_community = response.data.showCommunity;
                    }
                    
                    // Actualizar también en documentos compartidos si existe
                    const sharedDocIndex = this.sharedDocuments.findIndex(doc => doc.id === document.id);
                    if (sharedDocIndex !== -1) {
                        this.sharedDocuments[sharedDocIndex].show_community = response.data.showCommunity;
                    }
                    
                    this.filterAndSortLists();
                    this.$toast.success(response.data.message);
                } else {
                    this.$toast.error(response.data.error || 'No se pudo cambiar la privacidad del documento');
                }
            } catch (err) {
                console.error('Error al cambiar la privacidad del documento:', err);
                this.$toast.error('Error al cambiar la privacidad del documento');
            }
        }
    }
};
</script>

<style scoped>
.documents-container {
    min-height: 80vh;
    padding: 20px;
}

.document-table-container {
    /* Mantener este contenedor vacío si no necesitas bordes o estilos específicos */
}

:deep(.icon-column-table) {
    width: 40px !important;
}

:deep(.clickable-row) {
    cursor: pointer;
    transition: background-color 0.2s;
}

:deep(.clickable-row:hover) {
    background-color: rgba(0, 0, 0, 0.04) !important;
}

.document-card {
    height: 100%;
    width: 100%;
    max-width: 100%;
    transition: transform 0.2s;
    outline: rgba(218, 210, 181, 0.26) solid 1px !important
}

.sort-select {
    background-color: #f0f0f0;
    border-radius: 8px;
    padding: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.search-field >>> .v-input__slot {
    background-color: #ededed6e !important;
}

.search-field >>> fieldset {
    border: 1px solid #ededed6e !important;
}

.search-field >>> label {
	font-family: -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, Ubuntu, sans-serif !important;
    top: 1px !important;
}

.search-field >>> input {
    padding-top: 0px !important;
}

.search-field >>> svg {
    height: 18px;
    width: 18px;
}

.select-sort >>> .v-select__selection--comma {
  margin: 0 !important;
}
</style>
